import React from "react";
import { MapPin, X, Navigation } from "lucide-react";
import { Button } from "../shadcn/ui/button";
import { Label } from "../shadcn/ui/label";
import LocationPickerModal from "../screens/host-experience/host-add-space/components/LocationPickerModal";

/**
 * BookingLocationPicker
 *
 * Lets a customer set a booking location (e.g. a MOVE/vehicle pickup point) by Google Places
 * search OR a map pin-drop, reusing LocationPickerModal (which wraps InstanceLocationPicker in
 * a responsive Dialog/Drawer and handles the Google autocomplete click-outside quirks).
 *
 * Controlled component. `value` / `onChange` carry a pickup object:
 *   { label, latitude, longitude, placeId, digitalAddress, countryCode }
 * where latitude/longitude are STRINGS (the booking API expects "5.6350", not 5.6350).
 *
 * Props:
 *  - value: pickup object | null
 *  - onChange: (pickup | null) => void
 *  - label: section label (default "Pickup location")
 */

// Compose the pickup label: prefix the POI name (e.g. "KFC Achimota Mall") when available and
// not already the start of the formatted address, keeping the address for context.
const buildLocationLabel = coords => {
  const name = (coords?.name || "").trim();
  const address = (coords?.address || "").trim();
  if (name && (!address || !address.toLowerCase().startsWith(name.toLowerCase()))) {
    return address ? `${name}, ${address}` : name;
  }
  return address || name;
};

const BookingLocationPicker = ({ value, onChange, label = "Pickup location" }) => {
  // InstanceLocationPicker returns numeric lat/lng; the picker's initialValue expects numbers.
  // Seed placeId/countryCode too so re-confirming an unchanged pin preserves them (a genuine
  // new pick replaces the picker's selection wholesale, so stale values never leak).
  const initialValue =
    value && value.latitude !== undefined && value.longitude !== undefined
      ? {
          latitude: Number(value.latitude),
          longitude: Number(value.longitude),
          address: value.address || value.label,
          name: value.name || "",
          placeId: value.placeId || "",
          countryCode: value.countryCode || "",
        }
      : null;

  const handlePicked = coords => {
    if (!coords || coords.latitude == null || coords.longitude == null) return;
    const countryCode = coords.countryCode || "";
    onChange({
      label: buildLocationLabel(coords),
      latitude: String(coords.latitude),
      longitude: String(coords.longitude),
      placeId: coords.placeId || "",
      name: coords.name || "",
      address: coords.address || "",
      countryCode,
      digitalAddress: "",
    });
  };

  return (
    <div className="mt-4">
      <Label className="text-sm font-medium text-gray-700 mb-2 block">
        <MapPin className="w-4 h-4 inline mr-2" />
        {label}
      </Label>

      {value ? (
        <div className="space-y-2">
          <div className="flex items-start gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg text-sm">
            <Navigation className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium text-green-800 block truncate">
                {value.name || value.label || "Location selected"}
              </span>
              {value.address && <span className="text-xs text-green-600 truncate block">{value.address}</span>}
            </div>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label="Remove pickup location"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <LocationPickerModal
            value={initialValue}
            onChange={handlePicked}
            trigger={
              <Button type="button" variant="outline" size="sm">
                <MapPin className="w-4 h-4 mr-2" />
                Change location
              </Button>
            }
          />
        </div>
      ) : (
        <LocationPickerModal
          value={null}
          onChange={handlePicked}
          trigger={
            <Button type="button" variant="outline" className="w-full justify-start">
              <MapPin className="w-4 h-4 mr-2" />
              Add pickup location
            </Button>
          }
        />
      )}
    </div>
  );
};

export default BookingLocationPicker;
